import { useSuspenseQuery } from "@tanstack/react-query";
import { useBlocker } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  capitalize,
  EDRResponse,
  EDRResponses,
  queries,
  Technique,
  Test,
  useAuthConfig,
} from "@theprelude/core";
import {
  notifySuccess,
  InputField,
  DropdownTrigger,
  RotatingChevron,
  DropdownContent,
  Input,
  SearchIcon,
  IconButton,
  CloseIcon,
  DropdownRadio,
  DropdownRadioGroup,
  CheckmarkIcon,
  ResponseIcon,
  Dropdown,
  Button,
  Truncate,
} from "@theprelude/ds";
import { useState, useMemo, useRef, Suspense } from "react";
import { Controller, useForm, UseFormSetValue } from "react-hook-form";
import { usePopper } from "@/hooks/use-popper";
import { fuzzyFilter } from "@/lib/fuzzy-filters";
import { TestOverlayData } from "@/queries/tests";
import { Highlight } from "../highlight";
import {
  TestEditorField,
  formatEditableDetections,
  formatEditableThreatHunts,
} from "../test-editor/controlled-test-editor";
import {
  UpdateTestForm,
  useUpdateOwnerTestContents,
  useUpdatePreludeTestContents,
} from "./use-update-test";

function LoadingTestEdit() {
  return (
    <div className="flex h-full w-full flex-col gap-6 pt-4">
      <div className="space-y-4 px-4">
        <div className="h-4 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="h-7 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="h-4 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="h-9 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="h-4 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="h-7 w-2/3 animate-pulse rounded-md bg-secondary-foreground" />
        <div className="h-[300px] w-full animate-pulse rounded-md bg-secondary-foreground" />
      </div>
    </div>
  );
}

export default function TestEdit({
  props,
  setMode,
}: {
  props: TestOverlayData;
  setMode: (mode: "edit" | "view") => void;
}) {
  const { test } = props;

  if (test.account_id === "prelude") {
    return <PreludeTestEdit test={test} setMode={setMode} />;
  }
  return (
    <Suspense fallback={<LoadingTestEdit />}>
      <OwnerTestEdit props={props} setMode={setMode} />
    </Suspense>
  );
}

function OwnerTestEdit({
  props,
  setMode,
}: {
  props: TestOverlayData;
  setMode: (mode: "edit" | "view") => void;
}) {
  const authConfig = useAuthConfig();
  const [techniqueSearch, setTechniqueSearch] = useState<string>("");
  const [isCompiled, setIsCompiled] = useState<boolean>(true);
  const techniqueDropdown = usePopper();
  const edrResponseDropdown = usePopper();

  const { test, techniques, threatHunts, detections } = props;
  const { data: sourceCode } = useSuspenseQuery(
    queries.detect.getSourceCode(authConfig, test.id),
  );
  const { data: readme } = useSuspenseQuery(
    queries.detect.getReadMe(authConfig, test.id),
  );

  const orderedThreatHunts = useMemo(() => {
    return threatHunts.sort((a, b) => a.name.localeCompare(b.name));
  }, [threatHunts]);

  const { register, watch, setValue, handleSubmit, control, formState } =
    useForm<UpdateTestForm>({
      defaultValues: {
        testFields: {
          name: test.name,
          technique: test.technique ?? undefined,
          expectedEDRResponse: test.expected.crowdstrike,
        },
        testEditor: {
          sourceCode: sourceCode,
          detections: formatEditableDetections(detections),
          threatHunts: formatEditableThreatHunts(orderedThreatHunts),
          readme: readme,
          sourceTestId: test.id,
        },
      },
    });

  const filteredTechniques = useMemo(() => {
    return techniques?.filter((t) =>
      fuzzyFilter(`${t.id} - ${t.name}`, techniqueSearch),
    );
  }, [techniqueSearch, techniques]);

  const activeTechnique = techniques?.find(
    (t) => t.id === watch("testFields.technique"),
  );
  const edrResponsesOptions = Object.entries(EDRResponses).map(
    ([name, value]) => ({ name, value }),
  );
  const activeEDRResponse = edrResponsesOptions.find(
    (e) => e.value === watch("testFields.expectedEDRResponse"),
  );

  const updateTestContents = useUpdateOwnerTestContents();

  async function updateTest(form: UpdateTestForm) {
    updateTestContents.mutate(
      {
        test,
        readme,
        sourceCode,
        form,
      },
      {
        onSuccess: async () => {
          notifySuccess({
            message: "Successfully updated test",
          });
          setMode("view");
        },
      },
    );
  }

  useBlocker(
    () =>
      window.confirm("You have unsaved edits, are you sure you want to leave?"),
    formState.isDirty,
  );
  return (
    <form
      className="flex flex-col gap-4 p-4"
      onSubmit={handleSubmit(updateTest)}
    >
      <InputField label="Technique ID">
        <Dropdown {...techniqueDropdown}>
          <DropdownTrigger asChild>
            <div className="flex h-9 w-[195.5px] items-center justify-between rounded border border-stroke-input bg-surface-background px-3 py-2 text-sm focus:border-primary-foreground focus:outline-none">
              <span>{activeTechnique?.id ?? test.technique}</span>
              <RotatingChevron active={techniqueDropdown.open} />
            </div>
          </DropdownTrigger>
          <DropdownContent align="start" className="w-96">
            <Input
              className="rounded-none border-x-0 border-b border-t-0 border-stroke-line"
              value={techniqueSearch}
              onChange={(ev) => setTechniqueSearch(ev.target.value)}
              before={<SearchIcon />}
              after={
                <IconButton
                  icon={<CloseIcon />}
                  onClick={() => {
                    setTechniqueSearch("");
                  }}
                />
              }
              placeholder="Search by technique"
            />
            <TechniqueDropdown
              techniques={filteredTechniques}
              activeTechnique={activeTechnique}
              setValue={setValue}
              setTechniqueSearch={setTechniqueSearch}
              techniqueSearch={techniqueSearch}
            />
          </DropdownContent>
        </Dropdown>
      </InputField>
      <InputField label="Test Name">
        <Input {...register("testFields.name")} className="h-12 text-3xl" />
      </InputField>
      <InputField label="Expected Outcome">
        <Dropdown {...edrResponseDropdown}>
          <DropdownTrigger asChild>
            <div className="flex h-9 w-[195.5px] items-center gap-2 rounded border border-stroke-input bg-surface-background px-3 py-2 text-sm focus:border-primary-foreground focus:outline-none">
              <div className="flex w-full items-center justify-between">
                {activeEDRResponse ? (
                  <div className="flex items-center gap-1">
                    <ResponseIcon expectedResponse={activeEDRResponse.value} />
                    <span>
                      {capitalize(activeEDRResponse.name.toLowerCase())}
                    </span>
                  </div>
                ) : (
                  <>Unknown</>
                )}
                <RotatingChevron active={edrResponseDropdown.open} />
              </div>
            </div>
          </DropdownTrigger>
          <DropdownContent align="start">
            <DropdownRadioGroup
              defaultValue=""
              value={String(activeEDRResponse?.value ?? "")}
              onValueChange={(value) =>
                setValue(
                  "testFields.expectedEDRResponse",
                  Number(value) as EDRResponse,
                )
              }
              className="max-h-[300px] w-48 overflow-y-auto"
            >
              {edrResponsesOptions.map((edrResponse) => {
                if (edrResponse.value === EDRResponses.INVALID) return null;
                return (
                  <DropdownRadio
                    key={edrResponse.name}
                    value={String(edrResponse.value)}
                  >
                    <div className="flex items-center gap-2">
                      <ResponseIcon expectedResponse={edrResponse.value} />
                      <span>{capitalize(edrResponse.name.toLowerCase())}</span>
                    </div>
                  </DropdownRadio>
                );
              })}
            </DropdownRadioGroup>
          </DropdownContent>
        </Dropdown>
      </InputField>
      <Controller
        name="testEditor"
        control={control}
        render={({ field }) => (
          <TestEditorField
            state={field.value}
            onChange={field.onChange}
            isCompiled={isCompiled}
            setIsCompiled={setIsCompiled}
          />
        )}
      />
      <div className="flex items-center gap-4">
        <Button
          type="button"
          intent="secondary"
          onClick={() => setMode("view")}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          intent="primary"
          disabled={updateTestContents.isPending || !isCompiled}
          loading={updateTestContents.isPending}
        >
          Save
        </Button>
      </div>
    </form>
  );
}

function TechniqueDropdown({
  techniques,
  activeTechnique,
  setValue,
  setTechniqueSearch,
  techniqueSearch,
}: {
  techniques: Technique[];
  activeTechnique?: Technique;
  setValue: UseFormSetValue<UpdateTestForm>;
  setTechniqueSearch: (s: string) => void;
  techniqueSearch: string;
}) {
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: techniques.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent?.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });
  return (
    <DropdownRadioGroup
      ref={parentRef}
      defaultValue=""
      value={activeTechnique?.id}
      onValueChange={(value) => {
        setValue("testFields.technique", value);
        setTechniqueSearch("");
      }}
      className="h-[300px] overflow-y-auto"
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualItem) => {
          const technique = techniques[virtualItem.index] as Technique;
          const isActive = activeTechnique?.id === technique.id;
          return (
            <DropdownRadio
              key={technique.id}
              value={technique.id}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <div className="flex items-center justify-between gap-1">
                <Truncate text={`${technique.id} - ${technique.name}`}>
                  <Highlight
                    text={`${technique.id} - ${technique.name}`}
                    highlight={techniqueSearch}
                  />
                </Truncate>
                {isActive && <CheckmarkIcon />}
              </div>
            </DropdownRadio>
          );
        })}
      </div>
    </DropdownRadioGroup>
  );
}

const PreludeTestEdit = ({
  test,
  setMode,
}: {
  test: Test;
  setMode: (mode: "edit" | "view") => void;
}) => {
  const edrResponseDropdown = usePopper();
  const { watch, setValue, handleSubmit, formState } = useForm<{
    expectedEDRResponse: EDRResponse;
  }>({
    defaultValues: {
      expectedEDRResponse: test.expected.crowdstrike,
    },
  });
  const edrResponsesOptions = Object.entries(EDRResponses).map(
    ([name, value]) => ({ name, value }),
  );
  const activeEDRResponse = edrResponsesOptions.find(
    (e) => e.value === watch("expectedEDRResponse"),
  );

  const updateTestContents = useUpdatePreludeTestContents();

  function updateTest(formValues: { expectedEDRResponse: EDRResponse }) {
    updateTestContents.mutate(
      { test, expectedEDRResponse: formValues.expectedEDRResponse },
      {
        onSuccess: async () => {
          notifySuccess({
            message: "Successfully updated test",
          });
          setMode("view");
        },
      },
    );
  }

  useBlocker(
    () =>
      window.confirm("You have unsaved edits, are you sure you want to leave?"),
    formState.isDirty,
  );

  return (
    <form
      className="flex flex-col gap-4 p-4"
      onSubmit={handleSubmit(updateTest)}
    >
      <InputField label="Expected Outcome">
        <Dropdown {...edrResponseDropdown}>
          <DropdownTrigger asChild>
            <div className="flex h-9 w-[195.5px] items-center gap-2 rounded border border-stroke-input bg-surface-background px-3 py-2 text-sm focus:border-primary-foreground focus:outline-none">
              <div className="flex w-full items-center justify-between">
                {activeEDRResponse ? (
                  <div className="flex items-center gap-1">
                    <ResponseIcon expectedResponse={activeEDRResponse.value} />
                    <span>
                      {capitalize(activeEDRResponse.name.toLowerCase())}
                    </span>
                  </div>
                ) : (
                  <>Unknown</>
                )}
                <RotatingChevron active={edrResponseDropdown.open} />
              </div>
            </div>
          </DropdownTrigger>
          <DropdownContent align="start">
            <DropdownRadioGroup
              defaultValue=""
              value={String(activeEDRResponse?.value ?? "")}
              onValueChange={(value) =>
                setValue("expectedEDRResponse", Number(value) as EDRResponse)
              }
              className="max-h-[300px] w-48 overflow-y-auto"
            >
              {edrResponsesOptions.map((edrResponse) => {
                if (edrResponse.value === EDRResponses.INVALID) return null;
                return (
                  <DropdownRadio
                    key={edrResponse.name}
                    value={String(edrResponse.value)}
                  >
                    <div className="flex items-center gap-2">
                      <ResponseIcon expectedResponse={edrResponse.value} />
                      <span>{capitalize(edrResponse.name.toLowerCase())}</span>
                    </div>
                  </DropdownRadio>
                );
              })}
            </DropdownRadioGroup>
          </DropdownContent>
        </Dropdown>
      </InputField>

      <div className="flex items-center gap-4">
        <Button
          type="button"
          intent="secondary"
          onClick={() => setMode("view")}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          intent="primary"
          disabled={updateTestContents.isPending}
          loading={updateTestContents.isPending}
        >
          Save
        </Button>
      </div>
    </form>
  );
};
